// src/services/OrdersService.js
// Local Orders Service: generates a daily-resetting order number (001..),
// and provides a minimal local printing helper for per-item kitchen orders.

import { getValue, setValue } from '@/services/storageAdapter';
import { hardwareBridge } from '@/services/hardwareBridge';
import { Storage } from '@/services/Storage';
import axios from 'axios';

const KV_STORE = 'main_store';
const STATE_KEY = 'orders_service_state'; // { date: 'YYYY-MM-DD', counter: number }
const POLLING_INTERVAL = 3000; // 3 seconds
const POLLING_KEY = 'online_orders_polling_active';

function pad3(n) {
  n = Number(n || 0);
  if (n < 10) return `00${n}`;
  if (n < 100) return `0${n}`;
  return String(n);
}

function todayString() {
  const d = new Date();
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
}

async function loadState() {
  try {
    const s = await getValue(STATE_KEY, KV_STORE);
    if (s && typeof s === 'object') return s;
  } catch (e) {
    console.warn('[OrdersService] loadState failed:', e);
  }
  return { date: todayString(), counter: 0 };
}

async function saveState(state) {
  try {
    await setValue(STATE_KEY, state, KV_STORE);
  } catch (e) {
    console.warn('[OrdersService] saveState failed:', e);
  }
}

export const OrdersService = {
  _isTakeAwayEnabled(settings) {
    const raw = settings?.takeAwayOrder;
    if (typeof raw === 'boolean') return raw;
    if (typeof raw === 'number') return raw === 1;
    if (typeof raw === 'string') {
      const v = raw.trim().toLowerCase();
      return v === '1' || v === 'true' || v === 'yes' || v === 'on';
    }
    return false;
  },

  // Get the next order number (string: 001, 002, ...). Resets on new day.
  async nextOrderNumber() {
    const curDate = todayString();
    const state = await loadState();
    let counter = state.counter || 0;
    if (state.date !== curDate) {
      counter = 0; // reset for new day
    }
    counter += 1;
    const newState = { date: curDate, counter };
    await saveState(newState);
    return pad3(counter);
  },

  // Return current counter and date (without increment)
  async peekState() {
    return loadState();
  },

  // Render a small ESC/POS slip for a list of items, grouped by printer.
  // lines: array of sale lines
  // options: { tableName?, printerName? } options.printerName overrides individual item settings if provided (rarely used for kitchen orders)
  async printOrder(lines, options = {}) {
    try {
      if (!Array.isArray(lines) || lines.length === 0) return false;
      // Skip if we are not in Electron
      if (!(window?.hardwareIPC || window?.electronBridge)) return false;


      const separators = options.separators !== false; // default true
      const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== '';
      const normalizeSaleNumber = (v) => {
        if (v === undefined || v === null) return '';
        const s = String(v).trim();
        if (!s || s === '0') return '';
        return s;
      };
      let cashier = options.cashier;
      try {
        if (!cashier) {
          const u = await Storage.getCurrentUser();
          cashier = u?.Username || u?.username || '';
        }
      } catch (_) { }
      let deviceSettings = null;
      try {
        deviceSettings = await Storage.getDeviceSettings();
      } catch (_) {
        deviceSettings = null;
      }
      const takeAwayEnabled = deviceSettings ? this._isTakeAwayEnabled(deviceSettings) : true;

      // helpers
      const ESC = 0x1B;
      const GS = 0x1D;
      const cmdsPushText = (arr, text = '') => {
        for (const ch of String(text)) arr.push(ch.charCodeAt(0));
      };


      // Group items by printer
      // NOTE: We want separate slips per printer ID (e.g., "1", "2") even if they map to the same physical printer.
      const printerGroups = {}; // { "PrinterKey": { printerName, items } }

      for (const line of lines) {
        // Resolve printer name
        // Check various possible locations for the printer setting
        let printer = options.printerName;
        let printerId = '';

        if (!printer) {
          const setting = line.StockSetting || line.stockSetting;

          if (setting && setting.printer) {
            printer = setting.printer;
            printerId = String(setting.printer);
          } else if (line.Printer) {
            printer = line.Printer;
            printerId = String(line.Printer);
          } else if (line.printer) {
            printer = line.printer;
            printerId = String(line.printer);
          }
        }

        // Logic to map printer ID (e.g. "1") to actual printer name from settings
        if (printer && printer.length < 5) {
          if (!printerId) printerId = String(printer);
          const mapped = deviceSettings ? deviceSettings[`Printer${printer}`] : null;
          if (mapped) printer = mapped;
        }

        if (printer) {
          // If we have a printer ID (1..5), group by that ID.
          // Otherwise, fall back to physical printer name.
          const key = printerId ? `ID:${printerId}` : `NAME:${printer}`;
          if (!printerGroups[key]) {
            printerGroups[key] = { printerName: printer, items: [] };
          }
          printerGroups[key].items.push(line);
        } else {
          console.warn('[OrdersService] No printer resolved for line', {
            plu: line?.Plu || line?.PLU,
            printerId,
            linePrinter: line?.printer || line?.Printer,
            stockSettingPrinter: line?.StockSetting?.printer || line?.stockSetting?.printer
          });
        }
      }

      const printerNames = Object.keys(printerGroups);

      if (printerNames.length === 0) return false;

      const results = [];
      const resolvedSaleNumber = normalizeSaleNumber(
        options.saleNumber ?? options.SaleNum ?? options.saleNum
      );

      for (const groupKey of printerNames) {
        const group = printerGroups[groupKey];
        const items = group?.items || [];
        const printerName = group?.printerName;
        if (!items || items.length === 0) continue;

        let orderNo = options.orderNumber;
        let showSaleNumber = false;
        let saleNumberForPrint = '';
        // If Take Away is disabled, prioritize Sale Number so slip matches sale numbering.
        if (!takeAwayEnabled && hasValue(resolvedSaleNumber)) {
          orderNo = resolvedSaleNumber;
          showSaleNumber = true;
          saleNumberForPrint = resolvedSaleNumber;
        }

        // Last resort: generate a new order number
        if (!orderNo) {
          orderNo = await this.nextOrderNumber();
        }
        const now = new Date();
        const time = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const date = now.toLocaleDateString();
        const tableName = options.tableName || options.table || '';

        // Build ESC/POS commands
        const ESC = 0x1B;
        const GS = 0x1D;
        const cmds = [];

        // Init
        cmds.push(ESC, 0x40);

        // --- Header ---
        // Center + bold + scaled for header
        cmds.push(ESC, 0x61, 0x01);
        cmds.push(ESC, 0x45, 0x01); // Bold On
        cmds.push(GS, 0x21, 0x22); // Double Width & Height
        cmdsPushText(cmds, `ORDER ${orderNo}`);
        cmds.push(0x0A);
        cmds.push(0x0A);




        // Reset to normal after header
        cmds.push(GS, 0x21, 0x11); // Normal size
        cmds.push(ESC, 0x45, 0x00); // Bold Off
        cmds.push(ESC, 0x61, 0x00); // Left align

        // Info lines (moved to footer)
        if (tableName) {
          cmdsPushText(cmds, `Table: ${tableName}`);
          cmds.push(0x0A);
        }
        if (showSaleNumber) {
          cmdsPushText(cmds, `Sale No: ${saleNumberForPrint}`);
          cmds.push(0x0A);
        }


        // --- Items ---
        for (const item of items) {
          const name = (item.Description || item.StockDesc || item.desc || 'Item');
          const qty = (item.Qty || item.qty || 1);
          const notes = (item.Notes || item.note || item.Comment || '');
          const instructions = item.instructions || [];

          // Separator before each item (if enabled)
          if (separators) {
            cmds.push(GS, 0x21, 0x00); // Normal size
            cmds.push(ESC, 0x45, 0x00); // Bold Off
            cmdsPushText(cmds, '--------------------------------------------');
            cmds.push(0x0A);
          }

          // Item Name Line: Bold + scaled
          cmds.push(ESC, 0x45, 0x01); // Bold On
          cmds.push(GS, 0x21, 0x10); // Normal size
          cmdsPushText(cmds, `${qty} x ${name}`);
          // Back to normal size
          cmds.push(GS, 0x21, 0x00); // Normal size
          cmds.push(ESC, 0x45, 0x00); // Bold Off
          cmds.push(0x0A);

          // Notes / Instructions
          if (notes && notes.toString().trim().length > 0) {
            cmdsPushText(cmds, `  NOTE: ${notes.toString().trim()}`);
            cmds.push(0x0A);
          }

          if (Array.isArray(instructions)) {
            for (const inst of instructions) {
              if (inst && (inst.Message || inst.message || inst.Description || inst.CookingMsg)) {
                const msg = inst.Message || inst.message || inst.Description || inst.CookingMsg;
                const iQty = inst.count > 1 ? `${inst.count} x ` : '';
                cmdsPushText(cmds, `  - ${iQty}${msg}`);
                cmds.push(0x0A);
              }
            }
          }

        }

        // Final separator
        if (separators) {
          cmdsPushText(cmds, '--------------------------------------------');
          cmds.push(0x0A);
        }

        // --- Footer ---
        // Cashier and Date (moved from header)
        cmds.push(GS, 0x21, 0x11); // Normal size
        cmds.push(ESC, 0x45, 0x00); // Bold Off
        cmds.push(ESC, 0x61, 0x00);

        if (cashier) {
          cmdsPushText(cmds, `Cashier:${cashier}`);
          cmds.push(0x0A);
        }

        cmds.push(GS, 0x21, 0x00); // Normal size
        cmds.push(ESC, 0x45, 0x00); // Bold Off
        cmds.push(ESC, 0x61, 0x00);
        cmdsPushText(cmds, `Time: ${date} ${time}`);
        cmds.push(0x0A);

        // Feed lines and cut
        cmds.push(0x0A, 0x0A, 0x0A, 0x0A);
        // Partial cut
        cmds.push(GS, 0x56, 0x01);

        const bytes = Uint8Array.from(cmds);
        const base64 = btoa(String.fromCharCode.apply(null, bytes));

        const ok = await hardwareBridge.printBase64({ printer: printerName, base64, addFeed: false, addCut: false });
        results.push(ok);
      }

      return results.every(r => r === true);

    } catch (e) {
      console.warn('[OrdersService] printOrder failed:', e);
      return false;
    }
  },

  // Render a full Bill Slip (Not a Vat Invoice) locally
  async printBill(tableData, options = {}) {
    try {
      if (!tableData) return false;
      // Skip if we are not in Electron
      if (!(window?.hardwareIPC || window?.electronBridge)) return false;

      const items = tableData.items || [];
      if (items.length === 0) return false;

      // --- Prepare Metadata ---
      const settings = await Storage.getDeviceSettings(); // For Shop Name, Address, TPIN

      // Get Shop Details from settings or storage
      const shopName = settings.ShopName || 'Store'; // Fallback
      const address = settings.addr || settings.Address || '';
      const tpin = settings.TPIN || '';
      const phone = settings.Phone || '';

      const saleNum = tableData.saleNumber || tableData.SaleNum || '000';
      const tableName = tableData.Name || tableData.name || 'Table';

      const now = new Date();
      // Format: 15-12-2025 10:54:44
      const d = String(now.getDate()).padStart(2, '0');
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const y = now.getFullYear();
      const h = String(now.getHours()).padStart(2, '0');
      const min = String(now.getMinutes()).padStart(2, '0');
      const s = String(now.getSeconds()).padStart(2, '0');
      const printedTime = `${d}-${m}-${y} ${h}:${min}:${s}`;

      const waiter = tableData.user || tableData.User || '';

      // --- Calculate Totals ---
      let totalK = 0;
      items.forEach(item => {
        const lineVal = Number(item.Line) || 0;
        // Instruction prices are usually included in Line if calculated correctly in frontend,
        // but let's stick to item.Line as the source of truth for the bill total.
        // If instructions are separate in your logic, ensure they are added.
        // Based on sales-module, item.Line includes (Qty * Unit) - Discount.
        // It does NOT seem to include instruction prices in all versions of the code, 
        // but `TableDetails.vue` calculates tabTotal() by summing item.Line + instructions.

        totalK += lineVal;

        // Add instructions total manually if needed
        if (item.instructions && item.instructions.length > 0) {
          item.instructions.forEach(inst => {
            totalK += (Number(inst.Price || 0) * Number(inst.count || 1));
          });
        }
      });
      // Apply discount if any (usually applied line by line, but if there is a global discount not in lines??)
      // `tableData.discount` might exist?
      // For this implementation, we assume item.Line corresponds to the final price.
      // If there is an overall discount on the table object, handle it?
      // TableDetails.vue passes `discount: parseFloat(this.$store.getters.getSaleDiscount.total)` when saving.
      // But usually `item.Line` is what is printed.
      // Let's use the calculated totalK.

      // --- Currencies ---
      const currencies = await Storage.getStoreCurrencies() || [];
      // Calculate totals for each currency
      // Currency obj: { Symbol: '$', Rate: 22.0, ... }
      // Conversion: Total / Rate? Or Total * Rate? 
      // Usually: Local Currency (K) is Base. To get Foreign, divide by Rate? Or Multiply?
      // Checking currency-module: Base Currency Rate = 1.
      // E.g. Dollar Rate = 22.  1$ = 22K.  So K314 = 314 / 22 = $14.27.
      // Let's verify standard logic: AmountInForeign = AmountInBase / ExchangeRate.

      // --- ESC/POS Builders ---
      const ESC = 0x1B;
      const GS = 0x1D;
      const cmds = [];
      const cmdsPushText = (arr, text = '') => {
        for (const ch of String(text)) arr.push(ch.charCodeAt(0));
      };
      const center = () => { cmds.push(ESC, 0x61, 0x01); };
      const left = () => { cmds.push(ESC, 0x61, 0x00); };
      const boldOn = () => { cmds.push(ESC, 0x45, 0x01); };
      const boldOff = () => { cmds.push(ESC, 0x45, 0x00); };
      const separator = () => {
        left();
        cmdsPushText(cmds, '--------------------------------'); // 32 chars? Standard is 42 or 48. User showed 32 dashes.
        cmds.push(0x0A);
      };

      // Reset
      cmds.push(ESC, 0x40);

      // 1. Header (Centered)
      center();
      // boldOn(); // Optional for Store Name
      cmdsPushText(cmds, shopName);
      cmds.push(0x0A);

      if (address) {
        cmdsPushText(cmds, address);
        cmds.push(0x0A);
      }

      // Test line from user sample (maybe city or 2nd address line?)
      // We can skip or add if settings have it.

      cmds.push(0x0A); // Empty line

      // if (tpin) {
      //   cmdsPushText(cmds, `TPIN : ${tpin}`);
      //   cmds.push(0x0A);
      // }

      if (phone) {
        cmdsPushText(cmds, phone);
        cmds.push(0x0A);
      }

      separator();

      // 2. Title
      center();
      boldOn();
      cmdsPushText(cmds, 'Bill Receipt (Not a Vat Invoice)');
      cmds.push(0x0A);
      boldOff();

      separator();

      // 3. Meta Data (Left Aligned)
      left();
      cmdsPushText(cmds, `Sale #: ${saleNum}`);
      cmds.push(0x0A);
      cmdsPushText(cmds, `Table #: ${tableName}`);
      cmds.push(0x0A);
      cmdsPushText(cmds, `Printed Time: ${printedTime}`);
      cmds.push(0x0A);

      separator();

      // 4. Items
      // Format: "deep heat spray 150ml            1.0 x K314  K314"
      // Attempt to replicate spacing.
      // If 42-48 cols.
      items.forEach(item => {
        const name = item.Description || item.Name || 'Item';
        const qty = Number(item.Qty).toFixed(1);
        const unit = Number(item.Unit).toFixed(0); // User sample shows K314 (integer-like if no decimals? user sample has no decimals for K)
        const linePrice = Number(item.Line).toFixed(0);

        // Build the right hand side string first: "1.0 x K314  K314"
        const rhs = `${qty} x K${unit}  K${linePrice}`;

        // Logic: Print name, if too long wrap? Or try to fit on one line?
        // User sample shows Name on left, numbers on right.

        // Simple approach: Print Name line, then numbers line? OR aligned?
        // User sample looks like standard receipt alignment.
        // Let's try to fit on one line if short, else name on one, numbers on next?
        // User sample: "deep heat spray 150ml            1.0 x K314  K314"
        // This suggests one line with spacing.

        cmdsPushText(cmds, name);
        // If name is long, print newline?
        // Let's just print newline for right side to be safe or try to align if we knew width.
        // Assuming standard width, let's just push a few spaces and then the RHS
        // BUT if we want it to look good on narrow paper:
        // Print Name, Newline, Right Align RHS?
        // User sample has them on SAME LINE.

        /* 
           Simulate:
           Left Align Name (max X chars).
           Absolute position?
           Or just Space padding?
        */

        // Let's simplified approach: Name... Newline ... RHS (Right Aligned)
        // OR: Name (Left), RHS (Right) calculation.
        // Since we don't know exact char width (font A vs B), simplest solid method:
        // Name on one line.
        // Qty x Unit ... Total on second line (Right aligned).
        // BUT user sample shows 1 line.
        // Let's try 1 line with padding if name short, else 2.

        if (name.length < 20) {
          // Padding
          const pad = ' '.repeat(32 - name.length - rhs.length > 0 ? 32 - name.length - rhs.length : 1);
          cmdsPushText(cmds, pad + rhs);
        } else {
          // Name long, print it, then RHS on next line right aligned?
          // Or just append RHS.
          cmds.push(0x0A);
          // Right align RHS
          cmds.push(ESC, 0x61, 0x02); // Right
          cmdsPushText(cmds, rhs);
          left(); // Back to left
        }
        cmds.push(0x0A);

        // Instructions
        if (item.instructions && Array.isArray(item.instructions)) {
          item.instructions.forEach(inst => {
            const msg = inst.CookingMsg || inst.Message || inst.message || inst.Description || '';
            if (!msg) return;

            const count = Number(inst.count || inst.qty || 1);
            const price = Number(inst.Price || 0);
            const iCost = price * count;

            if (iCost > 0) {
              const unit = price.toFixed(0);
              const total = iCost.toFixed(0);
              cmdsPushText(cmds, ` + ${msg} (${count} x K${unit}) = K${total}`);
            } else {
              // Just print the message if no cost (cleaner)
              cmdsPushText(cmds, ` + ${msg}`);
            }
            cmds.push(0x0A);
          });
        }

      });

      separator();
      separator(); // Double separator in user sample

      // 5. Total Base
      // "Total Due (K): K314"
      boldOn();
      cmdsPushText(cmds, `Total Due (K): K${totalK.toFixed(0)}`); // User sample has no decimals for K
      cmds.push(0x0A);
      boldOff();

      cmds.push(0x0A); // Empty line

      // 6. Currencies
      /*
        Total Due ($): $14.273
        Total Due (R): R314
      */
      if (currencies.length > 0) {
        currencies.forEach(curr => {
          if (curr.Base) return; // Skip base (already printed)
          const rate = Number(curr.Rate);
          if (rate > 0) {
            const val = totalK / rate;
            // Symbol logic
            cmdsPushText(cmds, `Total Due (${curr.Symbol}): ${curr.Symbol}${val.toFixed(2)}`); // User showed 3 decimals for $? "$14.273". Let's stick to 2 unless requested specific. User sample has 3.
            cmds.push(0x0A);
          }
        });
      }

      cmds.push(0x0A);

      // 7. Tip & Total
      /*
        Tip   : ________________________
        
        Total : ________________________
      */
      cmdsPushText(cmds, 'Tip   : ________________________');
      cmds.push(0x0A, 0x0A);

      cmdsPushText(cmds, 'Total : ________________________');
      cmds.push(0x0A, 0x0A);

      // 8. Footer
      if (waiter) {
        cmdsPushText(cmds, `Waiter Name: ${waiter}`);
        cmds.push(0x0A);
      }
      cmdsPushText(cmds, 'Software by www.chrilantech.com');
      cmds.push(0x0A);

      // Feed & Cut
      cmds.push(0x0A, 0x0A, 0x0A, 0x0A);
      cmds.push(GS, 0x56, 0x01); // Cut

      const bytes = Uint8Array.from(cmds);
      const base64 = btoa(String.fromCharCode.apply(null, bytes));

      // Resolve Printer
      let printerName = options.printerName;
      if (!printerName) {
        const settings = await Storage.getDeviceSettings();
        printerName = settings.Printer || 'default';
      }

      const ok = await hardwareBridge.printBase64({ printer: printerName, base64, addFeed: false, addCut: false });
      return ok;

    } catch (e) {
      console.warn('[OrdersService] printBill failed:', e);
      return false;
    }
  },

  // ========== Online Orders Polling Service ==========

  _pollingIntervalId: null,

  // Transform online order format to printOrder format
  _transformOnlineOrder(onlineOrder) {
    try {
      const cookingItems = JSON.parse(onlineOrder.CookingIsnt || '[]');

      // Transform to the format expected by printOrder
      const lines = cookingItems.map(item => {
        const line = {
          Description: item.MainProductDes || 'Item',
          Qty: item.qty || 1,
          Notes: '',
          instructions: []
        };

        // Parse instructions if present
        if (item.instruction && Array.isArray(item.instruction)) {
          item.instruction.forEach(inst => {
            const instruction = {
              Message: inst.Message || inst.message || '',
              count: inst.Qty || inst.qty || 1,
              Price: 0 // Instructions may have price, add if needed
            };
            line.instructions.push(instruction);
          });
        }

        // Set printer if available
        if (item.ItemPrinter) {
          line.Printer = item.ItemPrinter;
        }

        return line;
      });

      return {
        lines,
        options: {
          tableName: onlineOrder.tableName || '',
          saleNumber: onlineOrder.saleNum || onlineOrder.SaleNum || '',
          cashier: onlineOrder.user || ''
        },
        orderId: onlineOrder.id
      };
    } catch (e) {
      console.warn('[OrdersService] Failed to transform online order:', e);
      return null;
    }
  },

  // Fetch pending online orders
  async _fetchPendingOrders() {
    try {
      const store = await Storage.getDefaultStore();
      if (!store || !store.StoreDB) {
        console.warn('[OrdersService] No store database found');
        return [];
      }

      const response = await axios.get(
        `${window.serverApi}cookinginstruction/${store.StoreDB}/logs/pending`
      );

      return response.data || [];
    } catch (e) {
      console.warn('[OrdersService] Failed to fetch pending orders:', e.message);
      return [];
    }
  },

  // Mark an order as printed
  async _markOrderAsPrinted(orderId) {
    try {
      const store = await Storage.getDefaultStore();
      if (!store || !store.StoreDB) {
        console.warn('[OrdersService] No store database found for marking as printed');
        return false;
      }

      await axios.put(
        `${window.serverApi}cookinginstruction/${store.StoreDB}/logs/${orderId}`,
        { printed: true }
      );

      return true;
    } catch (e) {
      console.warn('[OrdersService] Failed to mark order as printed:', e.message);
      return false;
    }
  },

  // Process a single online order
  async _processOnlineOrder(onlineOrder) {
    try {
      const transformed = this._transformOnlineOrder(onlineOrder);
      if (!transformed || !transformed.lines || transformed.lines.length === 0) {
        console.warn('[OrdersService] Invalid order data, skipping');
        return false;
      }

      // Print the order
      const printResult = await this.printOrder(transformed.lines, transformed.options);

      if (printResult) {
        console.log(`[OrdersService] Order ${transformed.orderId} printed successfully`);
      }

      return printResult;
    } catch (e) {
      console.warn('[OrdersService] Failed to process online order:', e);
      return false;
    }
  },

  // Poll for and process pending orders
  async _pollOrders() {
    try {
      const pendingOrders = await this._fetchPendingOrders();

      if (pendingOrders.length > 0) {
        console.log(`[OrdersService] Found ${pendingOrders.length} pending orders`);

        // Process each order
        for (const order of pendingOrders) {
          if (!order.printed) {
            await this._processOnlineOrder(order);
          }
        }
      }
    } catch (e) {
      console.warn('[OrdersService] Polling error:', e);
    }
  },

  // Start the online orders polling service
  startOnlineOrdersPolling() {
    // Check if already polling
    if (this._pollingIntervalId) {
      console.log('[OrdersService] Online orders polling already active');
      return;
    }

    console.log('[OrdersService] Starting online orders polling...');

    // Perform initial poll
    this._pollOrders();

    // Set up interval for subsequent polls
    this._pollingIntervalId = setInterval(() => {
      this._pollOrders();
    }, POLLING_INTERVAL);

    // Save polling state
    setValue(POLLING_KEY, true, KV_STORE);

    console.log(`[OrdersService] Online orders polling started (every ${POLLING_INTERVAL / 1000} seconds)`);
  },

  // Stop the online orders polling service
  stopOnlineOrdersPolling() {
    if (this._pollingIntervalId) {
      clearInterval(this._pollingIntervalId);
      this._pollingIntervalId = null;
      console.log('[OrdersService] Online orders polling stopped');
    }

    // Save polling state
    setValue(POLLING_KEY, false, KV_STORE);
  },

  // Check if polling is active
  isPollingActive() {
    return this._pollingIntervalId !== null;
  },

  // Restore polling state on service initialization
  async restorePollingState() {
    try {
      const isActive = await getValue(POLLING_KEY, KV_STORE);
      if (isActive) {
        this.startOnlineOrdersPolling();
      }
    } catch (e) {
      console.warn('[OrdersService] Failed to restore polling state:', e);
    }
  }
};

export default OrdersService;
